import React from 'react';
import CustomSelector from '../../../../shared/components/CustomSelector';
import Asterisk from '../../../../shared/components/Asterisk';
import ApolloError from '../../../../shared/components/ApolloError';
import { Collapse } from '@mui/material';
import CustomDatePicker from '../../../../shared/components/CustomDatePicker';
import WillingToTravelFields from '../../../../AppraiserForm/inputs/WillingToTravelFields';
import { useSelectionListOptions } from '../../../../AppraiserForm/hooks/useSelectionListOptions';
import { MAX_CHARS } from '../../../../AppraiserForm/constants/AppraiserInfoConstants';
import TextInputField from '../../../../AppraiserForm/inputs/TextInputField';
import {
  isAtlAffiliationEnabled,
  isAtmAffiliationEnabled
} from '../../../../AppraiserForm/services/AffiliationService';
import PrivilegeNames from '../../../../../constants/privilegeNames';
import { useReactiveVar } from '@apollo/client';
import { userInfo } from '@apollo-internal/cache';

/**
 * @typedef {object} AppraiserInfoProps
 * @property {Object} [appraiserData]
 * @property {(appraiserData: Object | ((prev: Object) => Object)) => void} [setAppraiserData]
 * @property {number|string} [appraiserSystemRoleId]
 */

/**
 * @param {AppraiserInfoProps} props
 */
const AppraiserInfo = props => {
  const { appraiserData = {}, setAppraiserData, appraiserSystemRoleId } = props;

  const currentUserInfo = useReactiveVar(userInfo);
  const showDirectChannel = currentUserInfo.Privileges.includes(
    PrivilegeNames.AppraiserProfile_DirectChannel_Edit
  );
  const showObservation = currentUserInfo.Privileges.includes(
    PrivilegeNames.AppraiserProfile_Observation_Edit
  );

  const {
    desiredRoleOptions,
    currentRoleOptions,
    timeZoneOptions,
    selectionListError,
    selectionLisLoading,
    directChannelOptions,
    observationOptions
  } = useSelectionListOptions(appraiserSystemRoleId);

  const handleFieldChange = event => {
    event.preventDefault();
    const name = event.target.name;
    const value = event.target.value;
    setValue(name, value);
  };

  const getCurrentRoleOption = value => {
    return currentRoleOptions.find(option => option.value === value);
  };

  const setCurrentRole = value => {
    const affiliationType = getCurrentRoleOption(value).affiliationType;
    setAppraiserData({
      ...appraiserData,
      affiliationType: affiliationType,
      currentRole: value,
      atlAffiliation: isAtlAffiliationEnabled(affiliationType)
        ? appraiserData.atlAffiliation
        : null,
      atmAffiliation: isAtmAffiliationEnabled(affiliationType) ? appraiserData.atmAffiliation : null
    });
  };

  const setValue = (name, value) => {
    setAppraiserData({ ...appraiserData, [name]: value });
  };

  if (selectionListError) {
    return <ApolloError />;
  }

  const currentRoleOption = getCurrentRoleOption(appraiserData.currentRole);

  return (
    <>
      <CustomSelector
        id='desired-roles-selector'
        title='Desired MDDAP Role'
        placeholder='Select a Role'
        options={desiredRoleOptions}
        value={appraiserData.desiredRole}
        setValue={value => setValue('desiredRole', value)}
        testId='desired-roles-selector'
        loading={selectionLisLoading}
      />

      <CustomSelector
        id='current-roles-selector'
        title='Current MDDAP Role'
        placeholder='Select a Role'
        options={currentRoleOptions}
        value={appraiserData.currentRole}
        setValue={setCurrentRole}
        testId='current-roles-selector'
        loading={selectionLisLoading}
      />

      {showDirectChannel && (
        <CustomSelector
          id='direct-channel-selector'
          title='Direct Channel'
          options={directChannelOptions}
          value={appraiserData.directChannel}
          setValue={value => setValue('directChannel', value)}
          testId='direct-channel-selector'
          loading={selectionLisLoading}
        />
      )}

      {showObservation && (
        <CustomSelector
          id='observation-selector'
          title='Observation'
          options={observationOptions}
          value={appraiserData.observation}
          setValue={value => setValue('observation', value)}
          testId='observation-selector'
          loading={selectionLisLoading}
        />
      )}

      <Collapse
        key='atmAffiliationCollapse'
        in={currentRoleOption && isAtmAffiliationEnabled(currentRoleOption.affiliationType)}
        unmountOnExit>
        <div data-testid='atm-affiliation-date'>
          <h4 className='field-title'>
            ATM Affiliation
            <Asterisk />
          </h4>

          <CustomDatePicker
            id='affiliated-since-date'
            selectedDate={appraiserData.atmAffiliation}
            setSelectedDate={value => setValue('atmAffiliation', value)}
          />
        </div>
      </Collapse>

      <Collapse
        key='atlAffiliationCollapse'
        in={currentRoleOption && isAtlAffiliationEnabled(currentRoleOption.affiliationType)}
        unmountOnExit>
        <div data-testid='atl-affiliation-date'>
          <h4 className='field-title'>
            ATL Affiliation
            <Asterisk />
          </h4>

          <CustomDatePicker
            id='affiliated-since-date'
            selectedDate={appraiserData.atlAffiliation}
            setSelectedDate={value => setValue('atlAffiliation', value)}
          />
        </div>
      </Collapse>

      <>
        <h4 className='field-title'>Location</h4>
        <TextInputField
          id='city'
          name='city'
          title='City'
          placeholder='Enter City'
          value={appraiserData.city}
          handleChange={handleFieldChange}
          maxLength={MAX_CHARS}
        />
        <TextInputField
          id='state'
          name='state'
          title='State'
          placeholder='Enter State/Province'
          value={appraiserData.state}
          handleChange={handleFieldChange}
          maxLength={MAX_CHARS}
        />
        <TextInputField
          id='country'
          name='country'
          title='Country'
          placeholder='Enter Country'
          value={appraiserData.country}
          handleChange={handleFieldChange}
          maxLength={MAX_CHARS}
        />
      </>

      <CustomSelector
        id='time-zones-selector'
        title='Time Zone'
        placeholder='Select a Time Zone'
        options={timeZoneOptions}
        value={appraiserData.timeZoneId}
        setValue={value => setValue('timeZoneId', value)}
        testId='time-zones-selector'
        loading={selectionLisLoading}
      />

      <WillingToTravelFields appraiserData={appraiserData} setValue={setValue} />
    </>
  );
};

export default AppraiserInfo;
